import React, { useState, useContext, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import BusinessProfile from './BusinessProfile';
import UserProfile from './UserProfile';
import Configurations from './Configurations';
import Team from './Team';
import SelfOptIn from './SelfOptIn';
import Billing from './Billing';
import ConfigureCampaigns from './campaign/ConfigureCampaigns';
import Container from '../shared/Container';
import Section from '../shared/Section';
import useMutateAnalytics from '../util/mutate.Analytics';
import analyticsKeys from '../util/UsageStatLinks';
import Tags from './Tags';
import IntegrationSettings from './IntegrationSettings';
import ROI from './ROI';
import HelpSidebar from './HelpSidebar';
import { UserContext } from '../context/UserContext';
import AccessLevels from '../util/AccessLevels';

const PageNavItems = Object.freeze({
  BusinessProfile: 'BusinessProfile',
  UserProfile: 'UserProfile',
  Configurations: 'Configurations',
  Team: 'Team',
  Billing: 'Billing',
  SelfOptIn: 'SelfOptIn',
  SmsCampaigns: 'SmsCampaigns',
  Tags: 'Tags',
  Integration: 'Integration',
  ROI: 'ROI',
});

export default function OrganizationSettings() {
  const [activeNav, setActiveNav] = useState(PageNavItems.BusinessProfile);
  const location = useLocation();
  const mutateAnalytics = useMutateAnalytics();
  const { user } = useContext(UserContext);
  const isSuperAdmin = user?.currentOrganizationRole === AccessLevels.SuperAdmin;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('settingsTab');
    if (tab === 'integration') {
      setActiveNav(PageNavItems.Integration);
    } else if (tab === 'selfOptIn') {
      setActiveNav(PageNavItems.SelfOptIn);
    }
  }, [location.search]);

  const pageHeader = [
    {
      text: 'Business Profile',
      onClick: () => {
        setActiveNav(PageNavItems.BusinessProfile);
        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_BUSINESS_PROFILE,
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.BusinessProfile,
    },
    {
      text: 'User Profile',
      onClick: () => {
        setActiveNav(PageNavItems.UserProfile);
        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_USER_PROFILE,
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.UserProfile,
    },
    {
      text: 'Referral Marketing',
      onClick: () => {
        setActiveNav(PageNavItems.Configurations);
        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_CONFIGURATIONS,
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.Configurations,
    },
    {
      text: 'SMS Campaigns',
      onClick: () => {
        setActiveNav(PageNavItems.SmsCampaigns);

        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_SMS_CAMPAIGNS,
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.SmsCampaigns,
      dataHelp: 'settings-sms-campaigns-tab',
    },
    {
      text: 'Integration',
      onClick: () => {
        setActiveNav(PageNavItems.Integration);
        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_INTEGRATION,
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.Integration,
      dataHelp: 'settings-integration-tab',
    },
    {
      text: 'Team',
      onClick: () => {
        setActiveNav(PageNavItems.Team);

        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_TEAM,
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.Team,
    },
    {
      text: 'Billing',
      onClick: () => {
        setActiveNav(PageNavItems.Billing);

        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_BILLINGS,
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.Billing,
    },
    {
      text: 'Self Opt-In',
      onClick: () => {
        setActiveNav(PageNavItems.SelfOptIn);

        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_SELF_OPT_IN,
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.SelfOptIn,
      dataHelp: 'settings-self-opt-in-tab',
    },
    {
      text: 'Tags',
      onClick: () => {
        setActiveNav(PageNavItems.Tags);
        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_CONTACT_TAGS,
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.Tags,
    },
    ...(isSuperAdmin ? [{
      text: 'ROI',
      onClick: () => {
        setActiveNav(PageNavItems.ROI);
        const mutateAnalyticsVariables = {
          link: analyticsKeys.SETTINGS_CONTACT_TAGS, // TODO: Add proper analytics key
        };
        mutateAnalytics(mutateAnalyticsVariables);
      },
      active: activeNav === PageNavItems.ROI,
    }] : []),
  ];

  return (
    <Container>
      <HelpSidebar />
      <Section
        headerTitle="Settings"
        toolbarItems={pageHeader}
        justify="flex-start"
        headerDirection="column"
        width="70%"
      >
        {(() => {
          switch (activeNav) {
          case PageNavItems.BusinessProfile:
            return <BusinessProfile />;
          case PageNavItems.UserProfile:
            return <UserProfile />;
          case PageNavItems.Configurations:
            return <Configurations />;
          case PageNavItems.Team:
            return <Team />;
          case PageNavItems.Billing:
            return <Billing />;
          case PageNavItems.SelfOptIn:
            return <SelfOptIn />;
          case PageNavItems.SmsCampaigns:
            return <ConfigureCampaigns />;
          case PageNavItems.Tags:
            return <Tags />;
          case PageNavItems.Integration:
            return <IntegrationSettings />;
          case PageNavItems.ROI:
            return <ROI />;
          default:
            return <BusinessProfile />;
          }
        })()}
      </Section>
    </Container>
  );
}
